import { Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild, ChangeDetectionStrategy, computed, signal } from '@angular/core'
import { form } from '@angular/forms/signals'
import { PageEvent } from '@angular/material/paginator'
import { Router } from '@angular/router'
import { Kayttaja, Roolit } from '@lemon/core-firebase/model/kayttaja'
import { Projekti } from '@lemon/core-firebase/model/kirjanpito-projektit'
import { TypeaheadToimittaja } from '@lemon/core-firebase/model/lasku'
import { Maksutapa, TositeHakutulos, TositeLaji, TositteetHakuPyynto, TositteetHakuVastaus } from '@lemon/core-firebase/model/tosite'
import { FirestoreIndeksoija } from '@lemon/core-firebase/service/firestore.indeksoija'
import { TositeUriService } from '@lemon/core-firebase/service/tosite/tosite-uri.service'
import { TuettuKieli } from '@lemon/core/model/common'
import { DateService } from '@lemon/core/service/date.service'
import { EMPTY, Subject, from, of } from 'rxjs'
import { debounceTime, map, switchMap, takeUntil } from 'rxjs/operators'
import { FirebaseLemonaid } from '../../../_angular/service/firebase-lemonaid.service'
import { KayttajaService } from '../../../_angular/service/kayttaja.service'
import { LaskuService } from '../../../_angular/service/lasku/lasku.service'
import { TositeHakuAikavali, TositeHakuStateService } from '../../../_angular/service/tosite-haku-state.service'
import { TositeService } from '../../../_angular/service/tosite/tosite.service'
import { arvonMuutokset, asetaArvo } from '../../../_jaettu-angular/_lomakkeet/arvon-muutokset'
import { ProjektitService } from '../../../_jaettu-angular/projektit.service'
import { LemonTranslationService } from '../../../_jaettu-angular/service/lemon-translation.service'
import { LocalStorageService } from '../../../_jaettu-angular/service/local-storage.service'
import { lueTositelistanEsitystapa, TositelistanEsitystapa, TositeListaProjekti, TositeListaRivi } from '../tosite-lista/tosite-lista.component'

interface MaksutapaChip {
  tunniste: number
  nimi: string
}

interface ProjektiChip {
  avain: string
  nimi: string
}

interface ToimittajaChip {
  avain: string
  nimi: string
}

type TositeHakuPaneeli = 'aikavali' | 'maksutapa' | 'toimittaja' | 'projekti' | 'tositelaji'

@Component({
  selector: 'app-tosite-haku',
  templateUrl: './tosite-haku.component.html',
  styleUrls: ['./tosite-haku.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class TositeHakuComponent implements OnInit, OnChanges, OnDestroy {
  @Input() avoinna = false
  @Input() oletusTositelaji: TositeLaji = 'tositteet'
  @Output() suljettu = new EventEmitter<void>()

  // Kenttä on @if-lohkon sisällä, joten setter laukeaa vasta kun dialogi avataan ja kenttä ilmestyy DOMiin
  @ViewChild('hakukentta', { read: ElementRef }) set hakukentta(kentta: ElementRef<HTMLInputElement> | undefined) {
    if (kentta) {
      setTimeout(() => kentta.nativeElement.focus())
    }
  }

  hakuteksti = form(signal(''))
  aikavali: TositeHakuAikavali = 'viimeiset12kk'
  readonly aikavaliPresetit: TositeHakuAikavali[] = ['viimeiset12kk', 'viimeiset3kk', 'viimeiset30pv', 'tamaVuosi', 'kaikki']
  alkuPvm = form(signal<Date | null>(null))
  loppuPvm = form(signal<Date | null>(null))
  readonly aikavalinTeksti = signal('')
  readonly avoinPaneeli = signal<TositeHakuPaneeli | null>(null)

  readonly kaikkiMaksutavat = signal<Maksutapa[]>([])
  valitutMaksutavat = new Set<number>()

  readonly kaikkiProjektit = signal<Projekti[]>([])
  readonly projektitMap = signal(new Map<string, TositeListaProjekti>())
  readonly valitutProjektit = signal<ReadonlySet<string>>(new Set<string>())
  readonly naytaProjektitSuodatin = signal(false)

  readonly kaikkiToimittajat = signal<TypeaheadToimittaja[]>([])
  valitutToimittajat = new Set<string>()

  readonly tositelaji = signal<TositeLaji>('tositteet')
  readonly naytaTositelajiValinta = signal(true)

  readonly tulokset = signal<TositeListaRivi[]>([])
  esitystapa: TositelistanEsitystapa = 'kuvilla'
  readonly haetaan = signal(false)
  readonly kokonaismaara = signal(0)
  readonly sivu = signal(0)
  sivunKoko = 50
  readonly sivukoot = [50, 100, 200]

  private readonly _maksutavatMap = computed(() => new Map(this.kaikkiMaksutavat().map(mt => [mt.i, mt])))
  private readonly _projektiNimet = computed(() => new Map(this.kaikkiProjektit().map(p => [p.avain, p.nimi])))
  private readonly _toimittajaNimet = computed(() => new Map(this.kaikkiToimittajat().map(t => [t.avain, t.nimi])))
  private _hakuTrigger = new Subject<void>()
  private _edellinenHakuJson: string | null = null
  private _ngUnsubscribe = new Subject<void>()
  private _nykyinenKieli: TuettuKieli | null = null
  private _asiakasId: string | null = null

  constructor(
    private _firebaseLemonaid: FirebaseLemonaid,
    private _dateService: DateService,
    private _tositeService: TositeService,
    private _projektitService: ProjektitService,
    private _kayttajaService: KayttajaService,
    private _laskuService: LaskuService,
    private _hakuStateService: TositeHakuStateService,
    private _router: Router,
    private _tositeUriService: TositeUriService,
    private _translationService: LemonTranslationService,
    private _indeksoija: FirestoreIndeksoija,
    private _localStorageService: LocalStorageService,
  ) {
    arvonMuutokset(this.hakuteksti).subscribe(() => this.hakutekstiMuuttui())
  }

  ngOnInit() {
    this._translationService.currentLanguageObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(kieli => {
      this._nykyinenKieli = kieli
      this._paivitaAikavalinTeksti()
    })

    this._hakuTrigger
      .pipe(
        debounceTime(500),
        switchMap(() => {
          if (!this.onkoHakuehtoja) {
            return of({ vastaus: null as TositteetHakuVastaus | null, pyyntoJson: null as string | null })
          }
          const pyynto = this._rakennaHakupyynto()
          const pyyntoJson = JSON.stringify(pyynto)
          // Ei lähetetä uutta pyyntöä, jos hakuehdot ovat samat kuin viimeksi sovelletussa vastauksessa
          if (pyyntoJson === this._edellinenHakuJson) {
            this.haetaan.set(false)
            return EMPTY
          }
          return from(this._suoritaHaku(pyynto)).pipe(map(vastaus => ({ vastaus, pyyntoJson })))
        }),
        takeUntil(this._ngUnsubscribe),
      )
      .subscribe(({ vastaus, pyyntoJson }) => this._kasitteleHakuvastaus(vastaus, pyyntoJson))

    const tallennettuHakutila = this._hakuStateService.annaHakutila()
    if (tallennettuHakutila) {
      asetaArvo(this.hakuteksti, tallennettuHakutila.hakuteksti, { ilmoita: false })
      this.aikavali = tallennettuHakutila.aikavali
      asetaArvo(this.alkuPvm, tallennettuHakutila.alkuPvm ? this._dateService.numberToDate(tallennettuHakutila.alkuPvm) : null)
      asetaArvo(this.loppuPvm, tallennettuHakutila.loppuPvm ? this._dateService.numberToDate(tallennettuHakutila.loppuPvm) : null)
      this.valitutMaksutavat = new Set(tallennettuHakutila.valitutMaksutavat)
      this.valitutToimittajat = new Set(tallennettuHakutila.valitutToimittajat.filter(avain => !!avain))
      this.valitutProjektit.set(new Set(tallennettuHakutila.valitutProjektit.filter(avain => !!avain)))
      this.tositelaji.set(tallennettuHakutila.tositelaji)
      this.sivu.set(tallennettuHakutila.sivu ?? 0)
      this.sivunKoko = tallennettuHakutila.sivunKoko ?? 50
      this._paivitaAikavalinTeksti()
      this._hakuStateService.tyhjennaHakutila()
      this._hakuTrigger.next()
    } else {
      this.tositelaji.set(this.oletusTositelaji)
    }

    this._kayttajaService.kayttajaObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(kayttaja => this._rajaaTositelajiRooleihin(kayttaja))

    this._tositeService.maksutavatObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(maksutavat => {
      this.kaikkiMaksutavat.set(maksutavat ?? [])
    })

    this._projektitService.naytaProjektitObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(nayta => {
      this.naytaProjektitSuodatin.set(nayta)
      if (!nayta && this.valitutProjektit().size > 0) {
        this.valitutProjektit.set(new Set())
        if (this.avoinPaneeli() === 'projekti') {
          this.avoinPaneeli.set(null)
        }
        this._haeAlusta()
      }
    })

    this._projektitService.naytettavatKirjanpitoprojektitObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(projektit => {
      const kaikkiProjektit = projektit ?? []
      this.kaikkiProjektit.set(kaikkiProjektit)
      this.projektitMap.set(new Map(kaikkiProjektit.map(p => [p.avain, { id: p.avain, nimi: p.nimi, ...this._projektitService.annaProjektinVarit(p.avain) }])))
    })

    this._kayttajaService.kayttajanTiedotObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(tiedot => {
      this._asiakasId = tiedot?.asiakasId ?? null
    })

    this._kayttajaService.kayttajanTiedotObservable
      .pipe(
        switchMap(tiedot => (tiedot ? this._laskuService.getTypeaheadToimittajat(tiedot) : of<TypeaheadToimittaja[]>([]))),
        takeUntil(this._ngUnsubscribe),
      )
      .subscribe(toimittajat => {
        this.kaikkiToimittajat.set((toimittajat ?? []).filter(t => !t.poistettu).sort((a, b) => (a.nimi ?? '').localeCompare(b.nimi ?? '', 'fi')))
      })
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!changes.avoinna) {
      return
    }
    if (changes.avoinna.currentValue) {
      this.esitystapa = lueTositelistanEsitystapa(this._localStorageService)
      this._hakuTrigger.next()
    } else {
      this.tulokset.set([])
      this.kokonaismaara.set(0)
      // Nollaus varmistaa, että uudelleen avattaessa haetaan tuoreet tulokset samoillakin ehdoilla
      this._edellinenHakuJson = null
      this._suljePaneelit()
    }
  }

  @HostListener('window:keydown.escape')
  suljeEscapella() {
    if (!this.avoinna) {
      return
    }
    if (this._onkoJokinPaneeliAuki()) {
      this._suljePaneelit()
    } else {
      this.sulje()
    }
  }

  @HostListener('document:click', ['$event'])
  dokumenttiaKlikattu(event: MouseEvent) {
    if (!this.avoinna || !this._onkoJokinPaneeliAuki()) {
      return
    }
    // composedPath säilyttää klikkauspolun, vaikka datepicker olisi jo irrottanut valitun päivän DOMista ennen kuin klikki kuplii dokumenttiin
    const klikattiinPaneelia = event.composedPath().some(solmu => solmu instanceof Element && (solmu.classList.contains('dropdown-wrap') || solmu.classList.contains('cdk-overlay-container')))
    if (klikattiinPaneelia) {
      return
    }
    this._suljePaneelit()
  }

  private _onkoJokinPaneeliAuki(): boolean {
    return this.avoinPaneeli() !== null
  }

  private _suljePaneelit() {
    this.avoinPaneeli.set(null)
  }

  sulje() {
    this.suljettu.emit()
  }

  taustaaKlikattu(event: MouseEvent) {
    if (event.target === event.currentTarget) {
      this.sulje()
    }
  }

  hakutekstiMuuttui() {
    this._haeAlusta()
  }

  sivuMuuttui(event: PageEvent) {
    this.sivu.set(event.pageIndex)
    this.sivunKoko = event.pageSize
    this._hakuTrigger.next()
  }

  // Ostolaskut avautuvat maksunäkymään, joka vaatii maksuroolin, ja tositteet tositenäkymään, joka vaatii tositeroolin
  private _rajaaTositelajiRooleihin(kayttaja: Kayttaja | null) {
    const roolit = kayttaja?.roolit
    const onMaksurooli = roolit?.[Roolit.MAKSUT] === true
    const onTositerooli = [Roolit.TOSITTEET_OSTO, Roolit.TOSITTEET_MYYNTI, Roolit.TOSITTEET_TILI, Roolit.TOSITTEET_PALKKA].some(rooli => roolit?.[rooli] === true)
    const pakotettuTositelaji: TositeLaji | null = onMaksurooli && !onTositerooli ? 'ostolaskut' : !onMaksurooli ? 'tositteet' : null
    this.naytaTositelajiValinta.set(!pakotettuTositelaji)
    if (pakotettuTositelaji && this.tositelaji() !== pakotettuTositelaji) {
      this.tositelaji.set(pakotettuTositelaji)
      if (this.avoinPaneeli() === 'tositelaji') {
        this.avoinPaneeli.set(null)
      }
      this._haeAlusta()
    }
  }

  private _haeAlusta() {
    this.sivu.set(0)
    this._hakuTrigger.next()
  }

  vaihdaPaneeli(paneeli: TositeHakuPaneeli) {
    this.avoinPaneeli.set(this.avoinPaneeli() === paneeli ? null : paneeli)
  }

  valitseAikavaliPreset(preset: TositeHakuAikavali) {
    this.aikavali = preset
    asetaArvo(this.alkuPvm, null)
    asetaArvo(this.loppuPvm, null)
    this.aikavalinTeksti.set('')
    this.avoinPaneeli.set(null)
    this._haeAlusta()
  }

  valitseAikavali() {
    if (!this.alkuPvm().value() && !this.loppuPvm().value()) {
      return
    }
    this.aikavali = 'mukautettu'
    this._paivitaAikavalinTeksti()
    this.avoinPaneeli.set(null)
    this._haeAlusta()
  }

  poistaAikavali() {
    this.valitseAikavaliPreset('kaikki')
  }

  onkoMaksutapaValittu(tunniste: number): boolean {
    return this.valitutMaksutavat.has(tunniste)
  }

  vaihdaMaksutapa(tunniste: number) {
    if (this.valitutMaksutavat.has(tunniste)) {
      this.valitutMaksutavat.delete(tunniste)
    } else {
      this.valitutMaksutavat.add(tunniste)
    }
    this._haeAlusta()
  }

  poistaMaksutapaValinta(tunniste: number) {
    this.valitutMaksutavat.delete(tunniste)
    this._haeAlusta()
  }

  tyhjennaMaksutavat() {
    if (this.valitutMaksutavat.size === 0) {
      return
    }
    this.valitutMaksutavat.clear()
    this._haeAlusta()
  }

  onkoToimittajaValittu(avain: string): boolean {
    return this.valitutToimittajat.has(avain)
  }

  vaihdaToimittaja(avain: string) {
    if (this.valitutToimittajat.has(avain)) {
      this.valitutToimittajat.delete(avain)
    } else {
      this.valitutToimittajat.add(avain)
    }
    this._haeAlusta()
  }

  poistaToimittajaValinta(avain: string) {
    this.valitutToimittajat.delete(avain)
    this._haeAlusta()
  }

  tyhjennaToimittajat() {
    if (this.valitutToimittajat.size === 0) {
      return
    }
    this.valitutToimittajat.clear()
    this._haeAlusta()
  }

  onkoProjektiValittu(avain: string): boolean {
    return this.valitutProjektit().has(avain)
  }

  vaihdaProjekti(avain: string) {
    const valitut = new Set(this.valitutProjektit())
    if (valitut.has(avain)) {
      valitut.delete(avain)
    } else {
      valitut.add(avain)
    }
    this.valitutProjektit.set(valitut)
    this._haeAlusta()
  }

  poistaProjektiValinta(avain: string) {
    const valitut = new Set(this.valitutProjektit())
    valitut.delete(avain)
    this.valitutProjektit.set(valitut)
    this._haeAlusta()
  }

  tyhjennaProjektit() {
    if (this.valitutProjektit().size === 0) {
      return
    }
    this.valitutProjektit.set(new Set())
    this._haeAlusta()
  }

  vaihdaTositelaji(arvo: TositeLaji) {
    if (this.tositelaji() === arvo) {
      this.avoinPaneeli.set(null)
      return
    }
    this.tositelaji.set(arvo)
    this.avoinPaneeli.set(null)
    this._haeAlusta()
  }

  get valitutMaksutavatLista(): MaksutapaChip[] {
    return Array.from(this.valitutMaksutavat).map(tunniste => ({
      tunniste,
      nimi: this._maksutavatMap().get(tunniste)?.l ?? '',
    }))
  }

  get valitutProjektitLista(): ProjektiChip[] {
    return Array.from(this.valitutProjektit()).map(avain => ({
      avain,
      nimi: this._projektiNimet().get(avain) ?? '',
    }))
  }

  get valitutToimittajatLista(): ToimittajaChip[] {
    return Array.from(this.valitutToimittajat).map(avain => ({
      avain,
      nimi: this._toimittajaNimet().get(avain) ?? '',
    }))
  }

  get onkoHakuehtoja(): boolean {
    return this.hakuteksti().value().trim().length > 0 || this.aikavali !== 'kaikki' || this.valitutMaksutavat.size > 0 || this.valitutProjektit().size > 0 || this.valitutToimittajat.size > 0
  }

  avaaTosite(tositteenAvain: string) {
    this._hakuStateService.asetaHakutila({
      hakuteksti: this.hakuteksti().value(),
      aikavali: this.aikavali,
      alkuPvm: this.alkuPvm().value() ? this._dateService.dateToNumber(this.alkuPvm().value()) : null,
      loppuPvm: this.loppuPvm().value() ? this._dateService.dateToNumber(this.loppuPvm().value()) : null,
      valitutMaksutavat: Array.from(this.valitutMaksutavat),
      valitutToimittajat: Array.from(this.valitutToimittajat),
      valitutProjektit: Array.from(this.valitutProjektit()),
      tositelaji: this.tositelaji(),
      sivu: this.sivu(),
      sivunKoko: this.sivunKoko,
      paluureitti: this._router.url,
    })
    this._hakuStateService.merkitseHakusivultaTulo()
    const onOstolasku = this.tulokset().find(rivi => rivi.id === tositteenAvain)?.onOstolasku
    if (onOstolasku) {
      this._router.navigate(['/maksut', tositteenAvain])
    } else {
      this._router.navigate(['/tositteet', 'osto', tositteenAvain])
    }
  }

  private async _suoritaHaku(pyynto: TositteetHakuPyynto): Promise<TositteetHakuVastaus | null> {
    this.haetaan.set(true)
    try {
      return await this._firebaseLemonaid.functionsCall<TositteetHakuPyynto, TositteetHakuVastaus>('tositteet-etsiTositeLemonSearch', pyynto, undefined, 'europe-north1')
    } catch {
      return null
    }
  }

  private _kasitteleHakuvastaus(vastaus: TositteetHakuVastaus | null, pyyntoJson: string | null) {
    this.haetaan.set(false)
    if (!vastaus || vastaus.e) {
      this.tulokset.set([])
      this.kokonaismaara.set(0)
      this._edellinenHakuJson = null
      return
    }
    // Merkitään vasta sovelletun vastauksen pyyntö, jotta switchMapin hylkäämä pyyntö ei estä uusintahakua
    this._edellinenHakuJson = pyyntoJson
    this.tulokset.set((vastaus.tositteet ?? []).map(t => this._luoListarivi(t)))
    this.kokonaismaara.set(vastaus.kokonaismaara ?? 0)
  }

  private _rakennaHakupyynto(): TositteetHakuPyynto {
    const pyynto: TositteetHakuPyynto = {}

    const teksti = this.hakuteksti().value().trim()
    if (teksti) {
      pyynto.vapaahaku = teksti

      const toimittajaAvaimet = this._annaTekstiaVastaavatToimittajaAvaimet(teksti)
      if (toimittajaAvaimet.length > 0) {
        pyynto.vapaahakuToimittajaAvaimet = toimittajaAvaimet
      }

      const projektiAvaimet = this._annaTekstiaVastaavatProjektiAvaimet(teksti)
      if (projektiAvaimet.length > 0) {
        pyynto.vapaahakuProjektiAvaimet = projektiAvaimet
      }
    }

    const today = this._dateService.currentNumberDate()
    switch (this.aikavali) {
      case 'viimeiset12kk':
        pyynto.alkuPvm = this._dateService.lisaaKuukausiaNumero(today, -12)
        pyynto.loppuPvm = today
        break
      case 'viimeiset3kk':
        pyynto.alkuPvm = this._dateService.lisaaKuukausiaNumero(today, -3)
        pyynto.loppuPvm = today
        break
      case 'viimeiset30pv':
        pyynto.alkuPvm = this._dateService.lisaaPaiviaNumber(today, -30)
        pyynto.loppuPvm = today
        break
      case 'tamaVuosi':
        pyynto.alkuPvm = this._dateService.dateToNumber(new Date(new Date().getFullYear(), 0, 1))
        pyynto.loppuPvm = today
        break
      case 'mukautettu':
        if (this.alkuPvm().value()) {
          pyynto.alkuPvm = this._dateService.dateToNumber(this.alkuPvm().value())
        }
        if (this.loppuPvm().value()) {
          pyynto.loppuPvm = this._dateService.dateToNumber(this.loppuPvm().value())
        }
        break
      case 'kaikki':
        break
    }

    if (this.valitutMaksutavat.size > 0) {
      pyynto.maksutavat = Array.from(this.valitutMaksutavat)
    }

    if (this.valitutProjektit().size > 0) {
      pyynto.projektiAvaimet = Array.from(this.valitutProjektit())
    }

    if (this.valitutToimittajat.size > 0) {
      pyynto.toimittajaAvaimet = Array.from(this.valitutToimittajat)
    }

    pyynto.tositelaji = this.tositelaji()
    pyynto.ylaraja = this.sivunKoko
    pyynto.sivu = this.sivu()

    return pyynto
  }

  private _annaTekstiaVastaavatToimittajaAvaimet(teksti: string): string[] {
    const hakusanat = this._annaHakusanat(teksti)
    return this.kaikkiToimittajat()
      .filter(toimittaja => this._nimiSisaltaaJonkinHakusanan(toimittaja.nimi, hakusanat))
      .map(toimittaja => toimittaja.avain)
  }

  private _annaTekstiaVastaavatProjektiAvaimet(teksti: string): string[] {
    const hakusanat = this._annaHakusanat(teksti)
    return this.kaikkiProjektit()
      .filter(projekti => this._nimiSisaltaaJonkinHakusanan(projekti.nimi, hakusanat))
      .map(projekti => projekti.avain)
  }

  private _annaHakusanat(teksti: string): string[] {
    return this._indeksoija
      .poistaValimerkitJaNormalisoiValilyonnit(teksti)
      .split(' ')
      .filter(sana => sana.length > 0)
  }

  private _nimiSisaltaaJonkinHakusanan(nimi: string | null | undefined, hakusanat: string[]): boolean {
    const normalisoituNimi = this._indeksoija.poistaValimerkitJaNormalisoiValilyonnit(nimi ?? '')
    return hakusanat.some(sana => normalisoituNimi.includes(sana))
  }

  private _luoListarivi(tosite: TositeHakutulos): TositeListaRivi {
    return {
      id: tosite.a,
      paivamaara: tosite.p ?? null,
      toimittajanNimi: (tosite.t ? this._toimittajaNimet().get(tosite.t) : null) ?? tosite.n ?? null,
      summa: tosite.s ?? null,
      maksutapa: this._maksutavatMap().get(tosite.m) ?? null,
      lisatiedot: tosite.e ?? '',
      projektinId: tosite.j ?? null,
      onOstolasku: tosite.l === 'o' || tosite.l === 'm',
      kansikuvaUrl: tosite.kk1 && this._asiakasId ? `/api/1/kuitit/kuvat/thumb/${this._tositeUriService.annaCloudStorageKuvaUri(this._asiakasId, tosite.kk1.kk, tosite.kk1.a, tosite.kk1.t)}` : null,
      kuvia: tosite.ku ?? 0,
    }
  }

  private _paivitaAikavalinTeksti() {
    if (!this.alkuPvm().value() && !this.loppuPvm().value()) {
      this.aikavalinTeksti.set('')
      return
    }

    if (!this._nykyinenKieli) {
      return
    }

    if (this.alkuPvm().value() && this.loppuPvm().value()) {
      const alkuStr = this._dateService.muotoilePaivaDate(this.alkuPvm().value(), this._nykyinenKieli)
      const loppuStr = this._dateService.muotoilePaivaDate(this.loppuPvm().value(), this._nykyinenKieli)
      this.aikavalinTeksti.set(`${alkuStr} - ${loppuStr}`)
    } else if (this.alkuPvm().value()) {
      const alkaenTeksti = this._translationService.lokalisoiKielella('tositteet.haku.alkaen', this._nykyinenKieli)
      const alkuStr = this._dateService.muotoilePaivaDate(this.alkuPvm().value(), this._nykyinenKieli)
      this.aikavalinTeksti.set(`${alkaenTeksti} ${alkuStr}`)
    } else if (this.loppuPvm().value()) {
      const paattyenTeksti = this._translationService.lokalisoiKielella('tositteet.haku.paattyen', this._nykyinenKieli)
      const loppuStr = this._dateService.muotoilePaivaDate(this.loppuPvm().value(), this._nykyinenKieli)
      this.aikavalinTeksti.set(`${paattyenTeksti} ${loppuStr}`)
    }
  }
}
